/**
 * PRP-MCP-5: pure helpers for the "from chat" marks on the estimate page.
 *
 * Chat (MCP) edits apply straight to the estimate. The change log says which
 * room and line each one touched; a line or room keeps its "from chat" tag
 * while a change on it is not undone. There is no review step (founder
 * decision D2, 2026-10-11).
 */
import { createContext, useContext } from 'react';

import type { McpChange } from '../../lib/mcpApi';
import type { ParsedEstimateResponse, ParsedLineItem, ParsedRoom } from '../../types';

/** A stored line's number suffix ("a" on an inserted line); not in the shared type yet. */
export function lineSuffixOf(li: ParsedLineItem): string {
  return (li as ParsedLineItem & { line_suffix?: string | null }).line_suffix ?? '';
}

/** The stored payload's version (B-7233) that GET /imports/{hash} returns. */
export function payloadVersionOf(res: ParsedEstimateResponse | null | undefined): string | undefined {
  return (res as (ParsedEstimateResponse & { payload_version?: string }) | null | undefined)?.payload_version;
}

/** Stable key for one line in one room. */
export function chatLineKey(
  roomCode: string,
  lineNumber: number | null | undefined,
  lineSuffix?: string | null,
): string {
  return `${roomCode}|${lineNumber ?? ''}|${lineSuffix ?? ''}`;
}

/** Which rooms and lines carry the "from chat" tag. */
export interface ChatIndex {
  lines: Set<string>;
  rooms: Set<string>;
}

export const EMPTY_CHAT_INDEX: ChatIndex = { lines: new Set(), rooms: new Set() };

/** True for a change that still marks the estimate (not undone). */
export function isOpenChatChange(c: McpChange): boolean {
  return !c.undone;
}

/** Builds the tag index from the change log. */
export function buildChatIndex(changes: McpChange[] | null | undefined): ChatIndex {
  if (!changes || changes.length === 0) return EMPTY_CHAT_INDEX;
  const lines = new Set<string>();
  const rooms = new Set<string>();
  for (const c of changes) {
    if (!isOpenChatChange(c) || !c.room_code) continue;
    if (c.line_ref) {
      lines.add(chatLineKey(c.room_code, c.line_ref.line_number, c.line_ref.line_suffix));
    } else if (c.kind === 'add_room') {
      rooms.add(c.room_code);
    }
  }
  return { lines, rooms };
}

/** True when this line was changed by chat and the change is not undone. */
export function isChatLine(index: ChatIndex, room: ParsedRoom, li: ParsedLineItem): boolean {
  return index.lines.has(chatLineKey(room.group_code, li.line_number, lineSuffixOf(li)));
}

/** True when chat added this room, or changed any line in it. */
export function roomHasChat(index: ChatIndex, room: ParsedRoom): boolean {
  return index.rooms.has(room.group_code) || room.line_items.some((li) => isChatLine(index, room, li));
}

/** The tag index and the "Only chat changes" filter, read by the list rows. */
export interface ChatMarks {
  index: ChatIndex;
  onlyChat: boolean;
}

export const ChatMarksContext = createContext<ChatMarks>({ index: EMPTY_CHAT_INDEX, onlyChat: false });

/** The page's chat marks (empty when the estimate has no chat changes). */
export function useChatMarks(): ChatMarks {
  return useContext(ChatMarksContext);
}

const TEXT_INPUT_TYPES = new Set([
  '',
  'text',
  'search',
  'number',
  'email',
  'tel',
  'url',
  'password',
  'date',
]);

/**
 * True while the user is in the middle of an edit: a text field has focus, or
 * a dialog, picker or menu is open. A refresh then would throw their work away.
 */
export function isEditInProgress(doc: Document = document): boolean {
  const el = doc.activeElement as HTMLElement | null;
  if (el) {
    const tag = el.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag === 'INPUT' && TEXT_INPUT_TYPES.has(((el as HTMLInputElement).type || '').toLowerCase())) {
      return true;
    }
    if (el.isContentEditable) return true;
  }
  return !!doc.querySelector('[role="dialog"], [role="listbox"], [role="menu"], [data-edit-open="true"]');
}

/** Formats an ISO time for the change log, or a dash. */
export function fmtChangeTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

/** One-line plain rendering of a before/after value object. */
export function fmtChangeValues(v: Record<string, unknown> | null | undefined): string {
  if (!v) return 'nothing';
  const parts = Object.entries(v)
    .filter(([, val]) => val !== null && val !== undefined && val !== '')
    .map(([k, val]) => `${k.replace(/_/g, ' ')} ${typeof val === 'object' ? JSON.stringify(val) : String(val)}`);
  return parts.length > 0 ? parts.join(', ') : 'nothing';
}
